import React from 'react';

const EPC_COLORS = {
  A: { bg: 'bg-green-600', text: 'text-white', label: 'A' },
  B: { bg: 'bg-green-500', text: 'text-white', label: 'B' },
  C: { bg: 'bg-lime-500', text: 'text-white', label: 'C' },
  D: { bg: 'bg-yellow-400', text: 'text-yellow-900', label: 'D' },
  E: { bg: 'bg-orange-400', text: 'text-white', label: 'E' },
  F: { bg: 'bg-orange-600', text: 'text-white', label: 'F' },
  G: { bg: 'bg-red-600', text: 'text-white', label: 'G' },
};

const TENURE_LABELS = {
  freehold: 'Freehold',
  leasehold: 'Leasehold',
  share_of_freehold: 'Share of Freehold',
  commonhold: 'Commonhold',
};

const FURNISHED_LABELS = {
  furnished: 'Furnished',
  part_furnished: 'Part Furnished',
  unfurnished: 'Unfurnished',
};

const PROPERTY_TYPE_LABELS = {
  detached: 'Detached House',
  semi_detached: 'Semi-Detached',
  terraced: 'Terraced',
  apartment: 'Apartment / Flat',
  penthouse: 'Penthouse',
  maisonette: 'Maisonette',
  bungalow: 'Bungalow',
  cottage: 'Cottage',
  villa: 'Villa',
  studio: 'Studio',
  house: 'House',
  commercial: 'Commercial',
  land: 'Land / Plot',
};

export const EpcBadge = ({ rating, size = 'sm' }) => {
  if (!rating) return null;
  const config = EPC_COLORS[rating.toUpperCase()] || EPC_COLORS.D;
  const sizeClass = size === 'lg'
    ? 'w-10 h-10 text-lg font-bold'
    : size === 'md'
    ? 'w-8 h-8 text-sm font-bold'
    : 'w-6 h-6 text-xs font-bold';
  return (
    <span
      className={`${config.bg} ${config.text} ${sizeClass} inline-flex items-center justify-center flex-shrink-0`}
      title={`EPC Rating ${config.label}`}
      data-testid="epc-badge"
    >
      {config.label}
    </span>
  );
};

export const EpcBar = ({ rating }) => {
  if (!rating) return null;
  const bands = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
  const idx = bands.indexOf(rating.toUpperCase());
  return (
    <div className="flex items-center gap-0.5" data-testid="epc-bar">
      {bands.map((b, i) => {
        const c = EPC_COLORS[b];
        const isActive = i === idx;
        return (
          <div
            key={b}
            className={`${c.bg} ${c.text} px-2 py-1 text-[10px] font-bold transition-all ${isActive ? 'ring-2 ring-[#0F172A] scale-110 z-10' : 'opacity-50'}`}
          >
            {b}
          </div>
        );
      })}
    </div>
  );
};

export const TenureBadge = ({ tenure }) => {
  if (!tenure) return null;
  const label = TENURE_LABELS[tenure] || tenure;
  const isFreehold = tenure === 'freehold';
  return (
    <span
      className={`px-3 py-1 text-xs font-semibold tracking-wider uppercase ${isFreehold ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}
      data-testid="tenure-badge"
    >
      {label}
    </span>
  );
};

export const FurnishedBadge = ({ status }) => {
  if (!status) return null;
  return (
    <span className="px-3 py-1 text-xs font-semibold tracking-wider uppercase bg-violet-50 text-violet-700 border border-violet-200" data-testid="furnished-badge">
      {FURNISHED_LABELS[status] || status}
    </span>
  );
};

export const PropertyTypeBadge = ({ type }) => {
  if (!type) return null;
  return (
    <span className="px-3 py-1 text-xs font-semibold tracking-wider uppercase bg-slate-100 text-slate-600" data-testid="property-type-badge">
      {PROPERTY_TYPE_LABELS[type] || type}
    </span>
  );
};

export const CouncilTaxBadge = ({ band }) => {
  if (!band) return null;
  return (
    <span className="px-3 py-1 text-xs font-semibold tracking-wider uppercase bg-blue-50 text-blue-700 border border-blue-200" data-testid="council-tax-badge">
      Council Tax Band {band}
    </span>
  );
};
